import React, { useEffect, useState } from "react";

import type { NewStoreFormValuesType } from "@hz/stealthy-gmc-cloaker-common/api-types/store";
import type { PlanNamesType } from "@hz/stealthy-gmc-cloaker-common/constants";
import { OnboardingSteps } from "@hz/stealthy-gmc-cloaker-common/constants";

import { Check, Plus, Store as StoreIcon, Trash2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
  useSteps,
  VStack,
} from "@chakra-ui/react";

import { PlanSelection } from "@components/PlanSelection";
import { StoreDetailsForm } from "@components/StoreDetailsForm";

import { useAddStoreMutation , useStartCheckoutMutation } from "@state/apiSlice/requests";
import type { AppDispatch } from "@state/store";
import { fetchInit , selectUserState } from "@state/userSlice/userSlice";

const steps = [
  {
    title: "Plan",
    description: "Choose your plan",
  },
  {
    title: "Stores",
    description: "Add your stores",
  },
  {
    title: "Contact Account Manager",
    description: "We'll handle the rest",
  },
];
export const OnboardingFlow = () => {
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const [createCheckoutSession] = useStartCheckoutMutation();

  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  const [addStores] = useAddStoreMutation();

  const { onboardingStep, accountId, userId, isLoading } = useSelector(selectUserState);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutSuccess = urlParams.get("checkout") === "success";

    if (checkoutSuccess) {
      // Clean up the URL to remove the query param
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState({}, document.title, url.toString());

      // Optional: refetch onboardingStep immediately
      dispatch(fetchInit({ forceRefetch: true }));
    }
  }, []);

  useEffect(() => {
    if (onboardingStep === OnboardingSteps.COMPLETED) {
      navigate("/", { replace: true });
      return;
    }

    if (
      onboardingStep === OnboardingSteps.CHOOSE_PLAN ||
      onboardingStep === OnboardingSteps.CHECKOUT_STARTED
    ) {
      setActiveStep(0);
    } else if (
      onboardingStep === OnboardingSteps.SUBSCRIBED ||
      onboardingStep === OnboardingSteps.SETUP_STORE
    ) {
      const newStore: NewStoreFormValuesType = {
        id: `store-${Date.now()}-${uuidv4()}`,
        domain: "",
        platform: "",
        countries: [],
      };
      setStores([newStore]);
      setEditingStore(newStore.id);
      setActiveStep(1);
    }
  }, [onboardingStep]);

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [stores, setStores] = useState<NewStoreFormValuesType[]>([]);
  const [editingStore, setEditingStore] = useState<string | null>(null);
  const [isFirstStore, setIsFirstStore] = useState(true);

  const handlePlanSelect = async (plan: PlanNamesType) => {
    try {
      setSelectedPlan(plan);
      if (accountId && userId && plan) {
        const data = await createCheckoutSession({ plan }).unwrap();

        if (data?.url) {
          if (window) {
            window.location.href = data.url; // Redirect to Stripe Checkout
          }
        } else {
          console.error("No URL returned for checkout session");
        }
      }
    } catch (error) {
      console.error("Failed to create checkout session:", error);
    }

    // setActiveStep(1);

    // const newStore: NewStoreFormValuesType = {
    //   id: `store-${Date.now()}-${uuidv4()}`,
    //   domain: "",
    //   platform: "",
    //   countries: [],
    // };
    // setStores([newStore]);
    // setEditingStore(newStore.id);
  };

  const handleDeleteStore = (storeId: string) => {
    setStores(stores.filter((store) => store.id !== storeId));
  };

  const handleAddStore = () => {
    const newStore: NewStoreFormValuesType = {
      id: `store-${Date.now()}-${uuidv4()}`,
      domain: "",
      platform: "",
      countries: [],
    };
    setStores([...stores, newStore]);
    setEditingStore(newStore.id);
  };
  const handleStoreSubmit = async (storeDetails: Partial<NewStoreFormValuesType>) => {
    if (editingStore) {
      setStores(
        stores.map((store) =>
          store.id === editingStore
            ? {
                ...store,
                ...storeDetails,
              }
            : store,
        ),
      );
      setEditingStore(null);
      setIsFirstStore(false);
    }
  };
  const handleComplete = () => {
    addStores(stores).then(() => {
      setActiveStep(2);
    });
  };

  const handleOpenDashboard = () => {
    dispatch(fetchInit({ forceRefetch: true })).then(() => {
      navigate("/", { replace: true });
    });
  };

  const addStoreBtnText = stores.length > 0 ? "Add Another Store" : "Add Store";

  const renderStoresStep = () => {
    if (editingStore || isFirstStore) {
      const store = stores.find((s) => s.id === editingStore);
      return (
        <Box
          maxW="2xl"
          mx="auto"
        >
          <StoreDetailsForm
            hideCancelButton={isFirstStore}
            onCancel={() => {
              setEditingStore(null);
              setIsFirstStore(false);
            }}
            selectedPlan={selectedPlan || ""}
            onBack={() => {
              if (isFirstStore) {
                setActiveStep(0);
                setStores([]);
              } else {
                setEditingStore(null);
              }
            }}
            onSubmit={handleStoreSubmit}
            showSteps={false}
            initialValues={store}
            showBackToPlan={isFirstStore}
          />
        </Box>
      );
    }
    return (
      <VStack
        spacing={8}
        maxW="2xl"
        mx="auto"
      >
        <Box
          textAlign="center"
          w="full"
        >
          <Heading
            size="xl"
            color="white"
            mb={2}
          >
            Add Your Stores
          </Heading>
          <Text color="gray.400">Add the stores you want to protect with your {selectedPlan} plan</Text>
        </Box>
        <VStack
          spacing={4}
          w="full"
        >
          {stores.map((store, index) => (
            <Box
              key={store.id}
              w="full"
              bg="gray.800"
              p={4}
              borderRadius="lg"
              border="1px"
              borderColor="gray.700"
            >
              <Flex
                justify="space-between"
                align="center"
              >
                <HStack spacing={4}>
                  <Icon
                    as={StoreIcon}
                    color="gray.400"
                  />
                  <VStack
                    align="start"
                    spacing={1}
                  >
                    <Text
                      color="white"
                      fontWeight="medium"
                    >
                      {store.domain || `Store ${index + 1}`}
                    </Text>
                    {store.platform && <Badge colorScheme="blue">{store.platform}</Badge>}
                  </VStack>
                </HStack>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="ghost"
                    color="gray.400"
                    onClick={() => setEditingStore(store.id)}
                    _hover={{
                      color: "white",
                    }}
                  >
                    {store.domain ? "Edit" : "Configure"}
                  </Button>
                  <IconButton
                    aria-label="Delete store"
                    icon={<Trash2 size={16} />}
                    size="sm"
                    variant="ghost"
                    color="red.400"
                    onClick={() => handleDeleteStore(store.id)}
                    _hover={{
                      bg: "whiteAlpha.100",
                    }}
                  />
                </HStack>
              </Flex>
            </Box>
          ))}
          <Button
            leftIcon={<Plus size={20} />}
            onClick={handleAddStore}
            variant="outline"
            borderColor="gray.700"
            color="gray.400"
            w="full"
            p={8}
            _hover={{
              bg: "gray.800",
              color: "white",
            }}
          >
            {addStoreBtnText}
          </Button>
        </VStack>
        <HStack
          justify="center"
          spacing={4}
          pt={4}
        >
          {/* <Button
            variant="ghost"
            color="gray.400"
            onClick={() => setActiveStep(0)}
            _hover={{
              color: 'white',
            }}
          >
            Back to Plan
          </Button> */}
          <Button
            bg="#ff3d00"
            color="white"
            onClick={handleComplete}
            isDisabled={stores.length === 0 || stores.some((store) => !store.domain)}
            _hover={{
              bg: "#e53700",
              boxShadow: "0 0 20px rgba(255, 61, 0, 0.5)",
            }}
          >
            Save & Continue
          </Button>
        </HStack>
      </VStack>
    );
  };
  const renderSuccessStep = () => (
    <VStack
      spacing={8}
      maxW="2xl"
      mx="auto"
      textAlign="center"
    >
      <Icon
        as={Check}
        boxSize={16}
        color="#ff3d00"
      />
      <VStack spacing={3}>
        <Heading
          size="xl"
          color="white"
        >
          Your Account Manager Has You Covered
        </Heading>
        <Text color="gray.400">We'll stealth parts of your store.</Text>
      </VStack>
      <Button
        bg="#ff3d00"
        color="white"
        size="lg"
        onClick={handleOpenDashboard}
        _hover={{
          bg: "#e53700",
          boxShadow: "0 0 20px rgba(255, 61, 0, 0.5)",
        }}
      >
        Go to Dashboard
      </Button>
    </VStack>
  );
  if (isLoading || onboardingStep === null) return null;

  return (
    <Box
      bg="gray.900"
      minH="100vh"
      p={8}
    >
      <Box
        maxW="8xl"
        mx="auto"
      >
        <Stepper
          index={activeStep}
          mb={12}
          colorScheme="red"
          sx={{
            "& .chakra-step__indicator": {
              borderColor: activeStep === 1 ? "#FF1300" : "gray.600",
            },
            "& .chakra-step__separator": {
              bg: activeStep === 1 ? "#FF1300" : "gray.600",
            },
            "& .chakra-step__title": {
              color: "white",
            },
            "& .chakra-step__description": {
              color: "gray.400",
            },
            '& [data-status="active"]': {
              "& .chakra-step__indicator": {
                borderColor: "#FF1300",
                bg: "#FF1300",
              },
            },
            '& [data-status="complete"]': {
              "& .chakra-step__indicator": {
                borderColor: "#FF1300",
                bg: "#FF1300",
              },
            },
          }}
        >
          {steps.map((step, index) => (
            <Step key={index}>
              <StepIndicator>
                <StepStatus
                  complete={<StepIcon />}
                  incomplete={<StepNumber />}
                  active={<StepNumber />}
                />
              </StepIndicator>
              <Box flexShrink="0">
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </Box>
              <StepSeparator />
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && (
          <PlanSelection
            onSelectPlan={handlePlanSelect}
            onClose={() => {}}
            showSteps={false}
          />
        )}
        {activeStep === 1 && renderStoresStep()}
        {activeStep === 2 && renderSuccessStep()}
      </Box>

      {import.meta.env.MODE !== "production" && (
        <Box
          w="full"
          justifyContent="flex-end"
          display="flex"
          gap={2}
        >
          <Button
            size="sm"
            onClick={() => setActiveStep(activeStep - 1)}
          >
            Prev (debug)
          </Button>

          <Button
            size="sm"
            onClick={() => setActiveStep(activeStep + 1)}
            disabled={activeStep >= steps.length}
          >
            Next (debug)
          </Button>
        </Box>
      )}
    </Box>
  );
};
